import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Chip,
  ErrorFillIcon,
  LinkOpenIcon,
  Modal,
  Text,
  Tooltip,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import { ModalContentsContainer, ModalLabelValueRow, ModalSection, Table } from '~/components/common';

import { usePutOrdersOrderIdMutation } from '~/hooks/delivery-agency/queries';
import { formatDate } from '~/utils/date';
import {
  getDeliveryAgencyOrderDeliveryInfoColumnDefs,
  getDeliveryAgencyProductNamesColumnDefs,
} from '~/utils/delivery-agency/table';

import {
  COLLECTING_REQUEST_LINK_PARAM,
  DATE_FORMAT,
  DELIVERY_AGENCY,
  DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS,
  DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS,
  DELIVERY_AGENCY_TOOLTIP_MESSAGE,
  PATH,
  TOAST_MESSAGE,
} from '~/constants';

import DeliveryAgencyOrderLocationField from './DeliveryAgencyOrderLocationField';
import useDeliveryAgencyOrderDetailForm from './useDeliveryAgencyOrderDetailForm';

import type { TypeDeliveryAgencyOrderDetail } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyOrderDetailModal.module.scss';

const SECTION_TABLE_HEIGHT = 244;

const EDITABLE_GUIDE_MESSAGE = '수령장소 및 출입방법 수정 시 출고번호가 같은 다른 주문에도 자동으로 함께 반영됩니다.';

/** 변경사항 없이 수정 시도 시 노출하는 얼럿 문구 */
const NO_CHANGES_ALERT_MESSAGE = '설정한 내용이 없습니다.\n설정 후 다시 시도해주세요.';

/** 수정 불가 안내 문구 — 편집 가능 상태(출고 대기/출고 진행중)를 bold로 강조 */
const NON_EDITABLE_GUIDE_MESSAGE = (
  <>
    {'수령장소 및 출입방법은 주문 상태가 '}
    <Text size="large" color="kurly" element="span" bold>
      출고 대기 또는 출고 진행중
    </Text>
    {'인 경우에만 수정 가능합니다.\n수정 시 출고번호가 같은 다른 주문에도 자동으로 함께 반영됩니다.'}
  </>
);

const DeliveryAgencyOrderDetailModal = ({
  open,
  orderId,
  data,
  onClose,
}: {
  open: boolean;
  /** 수정 API(PUT /orders/{orderId}) 호출용 주문 ID */
  orderId: number;
  data: TypeDeliveryAgencyOrderDetail;
  onClose: () => void;
}) => {
  // Table 이 입력 변경마다 다시 그려지지 않도록 data/columns 참조를 고정한다
  const productNamesData = useMemo(
    () => data.productNames.map((productName) => ({ productName })),
    [data.productNames]
  );
  const { columnDefs: productNamesColumnDefs } = useMemo(() => getDeliveryAgencyProductNamesColumnDefs(), []);
  const { columnDefs: deliveryInfoColumnDefs } = useMemo(() => getDeliveryAgencyOrderDeliveryInfoColumnDefs(), []);

  const {
    values,
    errors,
    isDeliveryLocationDetailRequired,
    isEntranceMethodDetailRequired,
    isDirty,
    deliveryLocationDetailRef,
    entranceMethodDetailRef,
    handleChangeDeliveryLocation,
    handleChangeEntranceMethod,
    handleChangeDeliveryLocationDetail,
    handleChangeEntranceMethodDetail,
    handleBlurDeliveryLocationDetail,
    handleBlurEntranceMethodDetail,
    validate,
  } = useDeliveryAgencyOrderDetailForm(data);

  const putOrderMutation = usePutOrdersOrderIdMutation();

  const navigate = useNavigate();

  const { orderInfoOutput, updatable, outboundCode, collectingHistories } = data;

  const hasCollectingHistory = collectingHistories.length > 0;

  /** 호버 시 회수건별로 반품접수일 / 반품요청번호 [회수 상태] 두 줄, 건 사이는 빈 줄 */
  const collectingHistoryDescription = collectingHistories
    .map(
      (history) =>
        `반품접수일 : ${formatDate(history.collectingRequestedDate, DATE_FORMAT.DATE) ?? '-'}\n` +
        `반품요청번호 : ${history.collectingRequestCode} [${history.collectingRequestStatus.description}]`
    )
    .join('\n\n');

  /** 반품접수일 범위를 함께 넘겨 이동한 화면이 그 조건으로 바로 검색되게 한다 */
  const handleClickCollectingHistory = () => {
    const dates = collectingHistories.map((history) => history.collectingRequestedDate).sort();
    const searchParams = new URLSearchParams({
      [COLLECTING_REQUEST_LINK_PARAM.OUTBOUND_CODE]: outboundCode,
      [COLLECTING_REQUEST_LINK_PARAM.DATE_FROM]: dates[0],
      [COLLECTING_REQUEST_LINK_PARAM.DATE_TO]: dates[dates.length - 1],
    });

    navigate(`${PATH.CLAIM_COLLECTING}?${searchParams.toString()}`);
  };

  const handleConfirm = () => {
    if (!updatable) {
      onClose();
      return;
    }

    if (!isDirty) {
      dialogService.addDialog({ children: NO_CHANGES_ALERT_MESSAGE });
      return;
    }

    const payload = validate();
    if (!payload) return;

    if (putOrderMutation.isPending) return;

    putOrderMutation.mutate(
      { orderId, ...payload },
      {
        onSuccess: () => {
          toastService.addToast({ message: '수정되었습니다.' });
          onClose();
        },
        onError: (error) => {
          // 61000(팝업 노출 대상 공통 에러)은 다이얼로그, 그 외 검증/상태 에러는 토스트로 노출
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({ children: error?.message });
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleCancel = () => {
    if (isDirty) {
      dialogService.addDialog({
        type: 'confirm',
        children: '작성된 내용이 저장되지 않습니다.\n페이지를 나가시겠습니까?',
        cancelText: '취소',
        confirmText: '나가기',
        onConfirm: onClose,
      });
      return;
    }

    onClose();
  };

  return (
    <Modal
      title="배송 주문 상세"
      size="xlarge"
      open={open}
      confirmText="확인"
      cancelText="취소"
      onConfirm={handleConfirm}
      onCancel={updatable ? handleCancel : undefined}
    >
      <ModalContentsContainer>
        <ModalSection title="주문 정보">
          <div className={styles['guide-banner']}>
            <ErrorFillIcon className={styles['guide-banner-icon']} size={16} color="#7E3AB0" />
            <Text className={styles['guide-banner-text']} size="large" color="kurly" element="span">
              {updatable ? EDITABLE_GUIDE_MESSAGE : NON_EDITABLE_GUIDE_MESSAGE}
            </Text>
          </div>
          <div className={styles['order-info-body']}>
            <div className={styles['order-info-column']}>
              <ModalLabelValueRow
                label="출고번호"
                value={
                  <div className={styles['outbound-code']}>
                    <Text size="large" element="span">
                      {outboundCode}
                    </Text>
                    {/* 반품요청번호가 26자 코드라 폭을 제한하면 회수 상태가 다음 줄로 넘어간다 */}
                    {hasCollectingHistory && (
                      <Tooltip description={collectingHistoryDescription}>
                        <Chip
                          size="small"
                          variant="stroke"
                          color="info"
                          icon={<LinkOpenIcon size={16} color="#0566cd" />}
                          iconPosition="right"
                          onClick={handleClickCollectingHistory}
                        >
                          회수 내역
                        </Chip>
                      </Tooltip>
                    )}
                  </div>
                }
              />
              <ModalLabelValueRow label="주문자명" value={orderInfoOutput.ordererName} />
              <ModalLabelValueRow label="수령자명" value={orderInfoOutput.receiverName} />
              <ModalLabelValueRow label="연락처" value={orderInfoOutput.receiverContact} />
              <ModalLabelValueRow
                label="알림톡 즉시 발송"
                tooltip={{ description: DELIVERY_AGENCY_TOOLTIP_MESSAGE.SEND_ALIM_TALK_IMMEDIATELY, maxWidth: '348px' }}
                value={orderInfoOutput.sendAlimTalkImmediately ? 'Y' : 'N'}
              />
              <ModalLabelValueRow label="기본주소" value={orderInfoOutput.primaryAddress} />
              <ModalLabelValueRow label="상세주소" value={orderInfoOutput.secondaryAddress || '-'} />
            </div>
            <div className={styles['order-info-column']}>
              <DeliveryAgencyOrderLocationField
                editable={updatable}
                selectLabel="수령장소"
                selectOptions={DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS}
                selectValue={values.deliveryLocation}
                selectError={errors.deliveryLocation}
                onSelectChange={handleChangeDeliveryLocation}
                detailLabel="수령장소 상세"
                detailRequired={isDeliveryLocationDetailRequired}
                detailValue={values.deliveryLocationDetail}
                detailError={errors.deliveryLocationDetail}
                detailRef={deliveryLocationDetailRef}
                onDetailChange={handleChangeDeliveryLocationDetail}
                onDetailBlur={handleBlurDeliveryLocationDetail}
              />
              <DeliveryAgencyOrderLocationField
                editable={updatable}
                selectLabel="출입방법"
                selectOptions={DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS}
                selectValue={values.entranceMethod}
                selectError={errors.entranceMethod}
                onSelectChange={handleChangeEntranceMethod}
                detailLabel="출입방법 상세"
                detailRequired={isEntranceMethodDetailRequired}
                detailValue={values.entranceMethodDetail}
                detailError={errors.entranceMethodDetail}
                detailRef={entranceMethodDetailRef}
                onDetailChange={handleChangeEntranceMethodDetail}
                onDetailBlur={handleBlurEntranceMethodDetail}
              />
            </div>
          </div>
        </ModalSection>
        <ModalSection title="상품 정보">
          <Table data={productNamesData} columns={productNamesColumnDefs} height={SECTION_TABLE_HEIGHT} />
        </ModalSection>
        <ModalSection title="배송 정보">
          <Table data={data.shippingLabelInfoOutputs} columns={deliveryInfoColumnDefs} height={SECTION_TABLE_HEIGHT} />
        </ModalSection>
      </ModalContentsContainer>
    </Modal>
  );
};

export default DeliveryAgencyOrderDetailModal;
